import { Copy, Check, RefreshCw, MessageSquareText } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { LinkifiedText } from '@/components/ui/linkified-text'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { SuggestedReplyCard, type Suggestion } from '@/components/conversation/suggested-reply-card'
import { ReassignMessageDropdown } from './reassign-message-dropdown'
import { formatISODateTime, getMessageChannel, getMessageSenderName } from '@/lib/format-utils'
import { useToast } from '@/hooks/use-toast'

/**
 * Shape of a single message this bubble reads - only the fields it
 * actually consumes.
 */
interface Message {
  id: string
  direction?: string
  bodyPlain?: string
  body_plain?: string
  body?: string
  raw_source?: string
  property_id?: string
  reservation_id?: string
  date?: string
  subject?: string
  channel?: string
  source?: string
  from?: string
}

interface MessageBubbleProps {
  message: Message
  guestDisplayName?: string
  suggestions?: Suggestion[]
  instructions?: string
  onRegenerate: (messageId: string, mode: string) => void
  onAddInstructions: (messageId: string) => void
  onReprocess?: (messageId: string) => void
  onReassign: (messageId: string, propertyId: string, reservationId: string | null) => void
  reprocessingMessageId?: string | null
  reassigningMessageId?: string | null
  streamingMessageId?: string | null
  copiedMessageId?: string | null
  onCopyMessage?: (messageId: string, body: string) => void
  isLastUnansweredGuestMessage?: boolean
  isSuggestionExpanded?: boolean
  onToggleSuggestion?: (messageId: string) => void
  onFeedbackUpdate?: () => void
}

export function MessageBubble({
  message,
  guestDisplayName,
  suggestions = [],
  instructions = '',
  onRegenerate,
  onAddInstructions,
  onReprocess,
  onReassign,
  reprocessingMessageId,
  reassigningMessageId,
  streamingMessageId,
  copiedMessageId,
  onCopyMessage,
  isLastUnansweredGuestMessage = false,
  isSuggestionExpanded = false,
  onToggleSuggestion,
  onFeedbackUpdate,
}: MessageBubbleProps) {
  const { toast } = useToast()

  const isGuest =
    message.direction === 'guest' ||
    message.direction === 'guest_to_host' ||
    message.direction === 'inbound' ||
    !message.direction

  // For guest messages, show "Guest" as sender label, but use reservation's guest name for display
  // When a message is in a reservation context, use the reservation's guest name instead of
  // the message's original sender name (which may be from a different reservation)
  const guestName = isGuest ? guestDisplayName || getMessageSenderName(message, 'Guest') : null
  const senderName = isGuest ? 'Guest' : 'Host'
  const channel = getMessageChannel(message)

  // Get message body - prefer bodyPlain, fallback to body_plain, then body
  const messageBody = message.bodyPlain || message.body_plain || message.body || ''

  const handleCopy = async () => {
    if (onCopyMessage) {
      onCopyMessage(message.id, messageBody)
    } else {
      try {
        await navigator.clipboard.writeText(messageBody)
        toast({
          title: 'Copied',
          description: 'Message copied to clipboard',
        })
      } catch {
        toast({
          title: 'Copy failed',
          description: 'Failed to copy message to clipboard',
          variant: 'destructive',
        })
      }
    }
  }

  return (
    <div className="space-y-3">
      <div className={`flex ${isGuest ? 'justify-end' : 'justify-start'} items-start gap-2`}>
        {/* Action buttons for guest messages */}
        {isGuest && (
          <div className="flex flex-col gap-5 mt-1.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleCopy}
                  className="text-textMuted hover:text-text transition-colors opacity-60 hover:opacity-100"
                >
                  {copiedMessageId === message.id ? (
                    <Check className="h-4 w-4 text-mint" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{copiedMessageId === message.id ? 'Copied!' : 'Copy message'}</p>
              </TooltipContent>
            </Tooltip>
            {message.raw_source && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => onReprocess?.(message.id)}
                    disabled={reprocessingMessageId === message.id}
                    className="text-textMuted hover:text-text transition-colors disabled:opacity-50 opacity-60 hover:opacity-100"
                  >
                    <RefreshCw
                      className={`h-4 w-4 ${
                        reprocessingMessageId === message.id ? 'animate-spin' : ''
                      }`}
                    />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Reprocess message from original email</p>
                </TooltipContent>
              </Tooltip>
            )}
            <ReassignMessageDropdown
              messageId={message.id}
              currentPropertyId={message.property_id}
              currentReservationId={message.reservation_id}
              onReassign={onReassign}
              isReassigning={reassigningMessageId === message.id}
            />
          </div>
        )}
        <div
          className={`group rounded-lg px-4 py-2.5 max-w-[70%] transition-opacity ${
            reprocessingMessageId === message.id ? 'opacity-60' : ''
          } ${isGuest ? 'bg-surface border border-border' : 'bg-surface border border-mint/30'}`}
        >
          {/* Header: Sender name and timestamp */}
          <div className="flex items-baseline justify-between gap-2 mb-1.5">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium">{senderName}</span>
              <Badge variant="secondary" className="text-xs px-1.5 py-0 h-4">
                {channel}
              </Badge>
            </div>
            {message.date && (
              <span className="text-xs text-textMuted whitespace-nowrap">
                {formatISODateTime(message.date)}
              </span>
            )}
          </div>
          {/* Show guest name where subject would be for guest messages */}
          {isGuest && guestName && <div className="text-xs font-medium mb-1.5">{guestName}</div>}
          {!isGuest && message.subject && message.subject.trim() && (
            <div className="text-xs text-textMuted mb-1.5 font-medium">{message.subject}</div>
          )}
          {messageBody && (
            <div className="text-sm whitespace-pre-wrap leading-relaxed">
              <LinkifiedText text={messageBody} />
            </div>
          )}
          {reprocessingMessageId === message.id && (
            <div className="mt-2 text-xs text-textMuted italic flex items-center gap-1">
              <RefreshCw className="h-3 w-3 animate-spin" />
              Reprocessing message...
            </div>
          )}
        </div>
      </div>
      {/* Suggested reply block for guest messages - only the thread's current unanswered
          guest message shows it inline; older messages stay collapsed behind a button so
          the feed doesn't fill up with "Not generated" placeholders as the conversation moves on. */}
      {isGuest && (isLastUnansweredGuestMessage || isSuggestionExpanded) && (
        <>
          <SuggestedReplyCard
            messageId={message.id}
            suggestions={suggestions}
            instructions={instructions}
            onRegenerate={onRegenerate}
            onAddInstructions={onAddInstructions}
            isStreaming={streamingMessageId === message.id}
            onFeedbackUpdate={onFeedbackUpdate}
          />
          {!isLastUnansweredGuestMessage && (
            <div className="flex justify-start">
              <button
                onClick={() => onToggleSuggestion?.(message.id)}
                className="mt-1 text-xs text-textMuted hover:text-text transition-colors opacity-60 hover:opacity-100"
              >
                Hide suggested reply
              </button>
            </div>
          )}
        </>
      )}
      {isGuest && !isLastUnansweredGuestMessage && !isSuggestionExpanded && (
        <div className="flex justify-start">
          <button
            onClick={() => onToggleSuggestion?.(message.id)}
            className="mt-1 flex items-center gap-1 text-xs text-textMuted hover:text-text transition-colors opacity-60 hover:opacity-100"
          >
            <MessageSquareText className="h-3 w-3" />
            {suggestions && suggestions.length > 0 ? 'View suggested reply' : 'Suggest reply'}
          </button>
        </div>
      )}
    </div>
  )
}
